import {
  ASCII_CHARS_REGEX,
  EMAIL_AND_PHONE_COMBINED_REGEX,
  EMAIL_REGEX,
  EMPTY_SPACES_REGEX,
  NUMBERS_REGEX
} from 'constants/utils';

import { Tab, Tabs } from '@mui/material';
import { Breadcrumbs, LoadingSpinner, Stack, Text } from 'components';
import { LinkWithIndirectRouting } from 'components/linkWithConfirm';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import {
  getLsUserRoleObject,
  getMobileInfoBannerClosed,
  setMobileInfoBannerClosed
} from 'modules/networkTools/localStorageTokens';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSites, getFavoriteZCodes } from 'store/clientProfile/clientProfileActions';
import {
  CareContact,
  getBillingProviders,
  getClient,
  getClientHistory,
  postCreateClient,
  putEditClient
} from 'store/clients/clientsActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getOtherScreenerDetails,
  getOtherScreenerHistory,
  getScreenerDetails,
  getScreenerHistory,
  getScreenerQuestions,
  getScreeners
} from 'store/screeener/screenerActions';
import {
  MSG_ASCII_CHARACTERS,
  MSG_FIELD_LESS_1000_CHARS,
  MSG_FIELD_LESS_100_CHARS,
  MSG_FIELD_LESS_20_CHARS,
  MSG_FIELD_LESS_220_CHARS,
  MSG_FIELD_LESS_40_CHARS,
  MSG_WHITE_SPACE_ONLY
} from 'utils/errorMessages';
import { addressSchema } from 'utils/validations/validations';
import * as Yup from 'yup';
import {
  getGoals,
  getNewGoalDraft,
  getNewTodoDraft,
  getPresets,
  getTodos
} from 'store/roadmaps/roadmapsActions';
import clsx from 'clsx';
import { useResponsive } from 'utils/hooks/useMediaQuery';
import { getBillingConfig } from 'store/clientProfile/clientProfileActions';

import { CareGap, resetClient, setChangeStatusModal } from '../../../store/clients/clientsSlice';
import { ProfileInfo } from './profileInfo';
import style from './clientForm.module.scss';

function useRouteMatch(patterns: readonly string[]) {
  const { pathname } = useLocation();

  let match;

  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];
    if (pathname.includes(pattern)) {
      match = pattern;
    }
  }

  return match;
}

const careContactSchema = Yup.object()
  .nullable()
  .shape({
    name: Yup.string()
      .nullable()
      .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
      .max(100, MSG_FIELD_LESS_100_CHARS),
    organization: Yup.string()
      .nullable()
      .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
      .max(100, MSG_FIELD_LESS_100_CHARS),
    phoneNumber: Yup.string()
      .nullable()
      .matches(EMAIL_AND_PHONE_COMBINED_REGEX, 'Phone number format is not valid.'),
    email: Yup.string()
      .nullable()
      .matches(EMAIL_REGEX, 'Email is not valid.')
      .max(100, MSG_FIELD_LESS_100_CHARS)
  });

const insuranceSchema = Yup.object().shape({
  otherInsuranceCategory: Yup.string().when('insuranceCategory', {
    is: 'OTHER',
    then: (schema) =>
      schema
        .required('Other Insurance Category is required.')
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
        .max(100, MSG_FIELD_LESS_100_CHARS),
    otherwise: (schema) => schema.notRequired()
  }),
  insuredDateOfBirth: Yup.string()
    .nullable()
    .test(
      'insuredDateOfBirth',
      'You can only add a date in the past',
      async (fieldVal): Promise<boolean | Yup.ValidationError | any> => {
        const today = dayjs();
        const selectedDate = dayjs(fieldVal);
        const isStartDateInPast = selectedDate.isAfter(today, 'd');

        return !isStartDateInPast;
      }
    ),
  insuredZipCode: Yup.string()
    .nullable()
    .matches(NUMBERS_REGEX, 'Please use valid numbers only.')
    .test('insuredZipCode', 'Must be 5 digits.', (val) => {
      if (val == null || val === '') {
        return true;
      }

      return val?.length === 5;
    }),
  insurancePlan: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  insuredName: Yup.string()
    .nullable()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  insuredStreetAddress: Yup.string()
    .nullable()
    .max(100, MSG_FIELD_LESS_100_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  insuredStreetAddress2: Yup.string()
    .nullable()
    .max(100, MSG_FIELD_LESS_100_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  insuredCity: Yup.string()
    .nullable()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  insuredState: Yup.string()
    .nullable()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  insuranceNumber: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(20, MSG_FIELD_LESS_20_CHARS),
  groupNumber: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(20, MSG_FIELD_LESS_20_CHARS)
});

export const validationSchema = Yup.object({
  firstName: Yup.string()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(EMPTY_SPACES_REGEX, MSG_WHITE_SPACE_ONLY)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .required('First name is required')
    .nullable(),
  lastName: Yup.string()
    .nullable()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(EMPTY_SPACES_REGEX, MSG_WHITE_SPACE_ONLY)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .required('Last name is required'),
  preferredName: Yup.string()
    .nullable()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  dob: Yup.string()
    .nullable()
    .required('DOB is required.')
    .test(
      'dob',
      'You can only add a date in the past',
      async (fieldVal): Promise<boolean | Yup.ValidationError | any> => {
        const today = dayjs();
        const selectedDate = dayjs(fieldVal);
        const isStartDateInPast = selectedDate.isAfter(today, 'd');

        return !isStartDateInPast;
      }
    ),
  clientIdentifier: Yup.string()
    .required('MRN or Customer ID is required.')
    .nullable()
    .max(40, MSG_FIELD_LESS_40_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  last4Ssn: Yup.string()
    .nullable()
    .matches(/^[0-9]+$/, 'Please use numbers only.')
    .min(4, 'Must be 4 digits.')
    .max(4, 'Must be 4 digits.'),
  language: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  genderIdentity: Yup.string().nullable(),
  races: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string()
          .nullable()
          .max(100, MSG_FIELD_LESS_100_CHARS)
          .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
        name: Yup.string()
          .nullable()
          // .max(100, MSG_FIELD_LESS_100_CHARS)
          .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
      })
    )
    .nullable(),
  selfDescribed: Yup.string()
    .nullable()
    .when('races', {
      is: (races: string[]) => races && races.includes('SELF_DESCRIBED'),
      then: (schema) =>
        schema.matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS).max(220, MSG_FIELD_LESS_220_CHARS)
    })
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(220, MSG_FIELD_LESS_220_CHARS),
  genderSelfDescribed: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  pronouns: Yup.string().nullable(),
  pronounsSelfDescribed: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  insuranceId: Yup.string().nullable(),
  insurancePlan: Yup.object({
    id: Yup.number().nullable(),
    name: Yup.string().nullable()
  }).nullable(),
  veteranStatus: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  primaryPhoneNumber: Yup.string()
    .nullable()
    .required('Primary Phone Number is required.')
    .matches(EMAIL_AND_PHONE_COMBINED_REGEX, 'Primary Phone Number is not valid.'),
  secondaryPhoneNumber: Yup.string()
    .nullable()
    .matches(EMAIL_AND_PHONE_COMBINED_REGEX, 'Secondary Phone Number is not valid.'),
  email: Yup.string()
    .nullable()
    .matches(EMAIL_REGEX, 'Email is not valid.')
    .max(100, MSG_FIELD_LESS_100_CHARS),

  primaryAddress: addressSchema.concat(
    Yup.object().shape({
      streetAddress: Yup.string()
        .nullable()
        .required('Street Address or Location is required.')
        .max(100, MSG_FIELD_LESS_100_CHARS)
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
      city: Yup.string()
        .nullable()
        .required('City is required.')
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
        .max(40, MSG_FIELD_LESS_40_CHARS),
      state: Yup.string()
        .nullable()
        .required('State is required.')
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
      zipCode: Yup.string()
        .nullable()
        .required('ZIP Code is required.')
        .matches(NUMBERS_REGEX, 'Please use valid numbers only.')
        .test('len', 'Must be 5 digits.', (val) => {
          if (val == null || val === '') {
            return true;
          }

          return val?.length === 5;
        })
    })
  ),
  sameAsPrimaryAddress: Yup.boolean().default(false),
  currentLocation: Yup.mixed().when('sameAsPrimaryAddress', {
    is: false,
    then: addressSchema,
    otherwise: Yup.mixed().notRequired()
  }),
  emergencyContacts: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .nullable()
        .max(100, MSG_FIELD_LESS_100_CHARS)
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
      phoneNumber: Yup.string()
        .nullable()
        .matches(EMAIL_AND_PHONE_COMBINED_REGEX, 'Phone Number is not valid.'),
      relationship: Yup.string().nullable(),
      relationshipOther: Yup.string()
        .nullable()
        .max(40, MSG_FIELD_LESS_40_CHARS)
        .matches(EMPTY_SPACES_REGEX, MSG_WHITE_SPACE_ONLY)
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    })
  ),
  insurances: Yup.array().of(
    Yup.object().shape({
      insuranceCategoryId: Yup.string().nullable(),
      insuranceCategoryName: Yup.string().nullable(),
      insurancePlan: Yup.string()
        .nullable()
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
        .max(100, MSG_FIELD_LESS_100_CHARS),
      insuranceNumber: Yup.string()
        .nullable()
        .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
        .max(20, MSG_FIELD_LESS_20_CHARS),
      endDate: Yup.string()
        .nullable()
        .test(
          'endDate',
          'End Date must be on or after Start Date.',
          async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
            const formikValues = values.parent;

            if (fieldVal == null || fieldVal === '') {
              return true;
            }

            console.log(formikValues.startDate);

            if (formikValues.startDate == null || formikValues.startDate === '') {
              return true;
            }
            //check if end time after start time
            let isStartTimeBeforeEndTime = true;
            const startTime = dayjs(`${formikValues.startDate}`);

            const endTime = dayjs(`${formikValues.endDate}`);

            isStartTimeBeforeEndTime = startTime.isSameOrBefore(endTime);

            return isStartTimeBeforeEndTime;
          }
        )
    })
  ),
  primaryInsurance: insuranceSchema,
  secondaryInsurance: insuranceSchema.nullable(),
  insuranceNotes: Yup.string()
    .nullable()
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS)
    .max(100, MSG_FIELD_LESS_100_CHARS),
  primaryCareProvider: careContactSchema,
  primaryCareCoordinator: careContactSchema,
  supportPeople: Yup.string().nullable().matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  careTeam: Yup.string().nullable().matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  referralClient: Yup.boolean()
    .typeError('Referral Client is required.')
    .required('Referral Client is required.'),
  referralDate: Yup.string()
    .nullable()
    .when('referralClient', {
      is: true,
      then: Yup.string().nullable().required('Referral Date is required when client is referred.')
    })
    .test('referralDate', 'Referral Date cannot be set in future.', (referralDate) => {
      const now = dayjs();
      return !dayjs(referralDate).isAfter(now, 'day');
    }),
  referredBy: Yup.string()
    .nullable()
    .max(100, MSG_FIELD_LESS_100_CHARS)
    .matches(EMPTY_SPACES_REGEX, MSG_WHITE_SPACE_ONLY)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  referrerPhone: Yup.string()
    .nullable()
    .matches(EMAIL_AND_PHONE_COMBINED_REGEX, 'Phone number format is not valid.'),
  referrerEmail: Yup.string()
    .nullable()
    .matches(EMAIL_REGEX, 'Email is not valid.')
    .max(100, MSG_FIELD_LESS_100_CHARS),
  // .when('referralClient', {
  //   is: true,
  //   then: Yup.string().nullable().required('Referring Provider Email is required.')
  // }),
  referrerNotes: Yup.string()
    .nullable()
    .max(1000, MSG_FIELD_LESS_1000_CHARS)
    .matches(ASCII_CHARS_REGEX, MSG_ASCII_CHARACTERS),
  billing: Yup.object().shape({
    billingPrograms: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().nullable(),
        startDate: Yup.string().nullable(),
        endDate: Yup.string()
          .nullable()
          .test(
            'endDate',
            'End Date should be on or after Start Date',
            async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
              const formikValues = values.parent;

              if (fieldVal == null || fieldVal === '') {
                return true;
              }

              if (formikValues.startDate == null || formikValues.startDate === '') {
                return true;
              }

              // Check if end date is on or after start date
              const startTime = dayjs(`${formikValues.startDate}`);
              const endTime = dayjs(`${formikValues.endDate}`);
              const isEndDateOnOrAfterStartDate = startTime.isSameOrBefore(endTime);

              return isEndDateOnOrAfterStartDate;
            }
          )
      })
    ),
    orderingProviders: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().nullable(),
        startDate: Yup.string().nullable(),
        endDate: Yup.string()
          .nullable()
          .test(
            'endDate',
            'End Date should be on or after Start Date',
            async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
              const formikValues = values.parent;

              if (fieldVal == null || fieldVal === '') {
                return true;
              }

              if (formikValues.startDate == null || formikValues.startDate === '') {
                return true;
              }

              // Check if end date is on or after start date
              const startTime = dayjs(`${formikValues.startDate}`);
              const endTime = dayjs(`${formikValues.endDate}`);
              const isEndDateOnOrAfterStartDate = startTime.isSameOrBefore(endTime);

              return isEndDateOnOrAfterStartDate;
            }
          )
      })
    ),
    billingSites: Yup.array().of(
      Yup.object().shape({
        siteId: Yup.string().nullable(),
        startDate: Yup.string().nullable(),
        endDate: Yup.string()
          .nullable()
          .test(
            'endDate',
            'End Date should be on or after Start Date',
            async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
              const formikValues = values.parent;

              if (fieldVal == null || fieldVal === '') {
                return true;
              }

              if (formikValues.startDate == null || formikValues.startDate === '') {
                return true;
              }

              // Check if end date is on or after start date
              const startTime = dayjs(`${formikValues.startDate}`);
              const endTime = dayjs(`${formikValues.endDate}`);
              const isEndDateOnOrAfterStartDate = startTime.isSameOrBefore(endTime);

              return isEndDateOnOrAfterStartDate;
            }
          )
      })
    )
  }),
  sex: Yup.string().nullable().required('Sex is required.')
});

export type Insurance = {
  insuredSameAsClient: boolean;
  insuranceCategory: string;
  otherInsuranceCategory: string;
  insurancePlan: string;
  insuranceNumber: string;
  groupNumber: string;
  relationshipToClient: string;
  insuredName: string;
  insuredDateOfBirth: string;
  insuredStreetAddress: string;
  insuredStreetAddress2: string;
  insuredCity: string;
  insuredState: string;
  insuredZipCode: string;
};

export type ZCode = {
  id: string;
  code: string;
  description: string;
};

export type BillingProgram = {
  billingProgramId: string;
  startDate: string;
  endDate: string;
};

export type OrderingProvider = {
  orderingProviderId: string;
  startDate: string;
  endDate: string;
};

export type BillingSite = {
  siteId: string;
  startDate: string;
  endDate: string;
};

export type RaceType = {
  id: string;
  name: string;
  race: string;
  description: string;
};

const initialValues = {
  careGaps: [] as CareGap[],
  firstName: null,
  lastName: null,
  preferredName: null,
  dob: null,
  clientIdentifier: null,
  last4Ssn: null,
  language: null,
  sex: null,
  genderIdentity: null,
  genderSelfDescribed: null,
  pronouns: null,
  pronounsSelfDescribed: null,
  insuranceId: null,
  insurancePlan: null as {
    id: number;
    name: string;
  } | null,
  veteranStatus: null,
  races: null as RaceType[] | null,
  selfDescribed: null,
  primaryPhoneNumber: null,
  secondaryPhoneNumber: null,
  email: null,
  primaryAddress: {
    streetAddress: null,
    streetAddress2: null,
    city: null,
    state: null,
    zipCode: null,
    building: null,
    floor: null,
    room: null
  },
  sameAsPrimaryAddress: false,
  currentLocation: {
    streetAddress: null,
    streetAddress2: null,
    city: null,
    state: null,
    zipCode: null,
    building: null,
    floor: null,
    room: null
  },
  emergencyContacts: [
    { name: null, phoneNumber: null, relationship: null, relationshipOther: null }
  ],
  primaryInsurance: {
    insuredSameAsClient: true,
    insuranceCategory: '',
    otherInsuranceCategory: '',
    insurancePlan: '',
    insuranceNumber: '',
    groupNumber: '',
    // if insuredSameAsClient is false
    relationshipToClient: '',
    insuredName: '',
    insuredDateOfBirth: '',
    insuredStreetAddress: '',
    insuredStreetAddress2: '',
    insuredCity: '',
    insuredState: '',
    insuredZipCode: ''
  },
  billing: {
    orderingProviders: [
      {
        orderingProviderId: '',
        startDate: '',
        endDate: ''
      }
    ] as OrderingProvider[],
    primaryMedicalDiagnosis: '',
    acuity: '',
    zCodes: [
      {
        id: '',
        code: '',
        description: ''
      }
    ] as ZCode[],
    billingPrograms: [
      {
        billingProgramId: '',
        startDate: '',
        endDate: ''
      }
    ] as BillingProgram[],
    billingSites: [
      {
        siteId: '',
        startDate: '',
        endDate: ''
      }
    ] as BillingSite[]
  },
  insurances: [],
  secondaryInsurance: null as Insurance | null,
  insuranceNotes: '',
  primaryCareProvider: null as CareContact | null,
  primaryCareCoordinator: null as CareContact | null,
  supportPeople: null,
  careTeam: null,
  referralClient: null,
  referralDate: null,
  referredBy: null,
  referrerPhone: null,
  referrerEmail: null,
  referrerNotes: null,
  duplicatedClient: null
};

export type FormValues = typeof initialValues;

const useMobileInfoBanner = () => {
  const [isMobileInfoBannerClosed, setIsMobileInfoBannerClosed] = useState(
    getMobileInfoBannerClosed()
  );

  const handleCloseMobileInfoBanner = (value: boolean) => {
    setIsMobileInfoBannerClosed(value);
    setMobileInfoBannerClosed(value);
  };

  return { isMobileInfoBannerClosed, setIsMobileInfoBannerClosed: handleCloseMobileInfoBanner };
};

function sanitizeClientDTO(client: FormValues) {
  const sanitizedClient = { ...client };
  // Recursively sanitize string fields
  Object.keys(sanitizedClient).forEach((key) => {
    const value = (sanitizedClient as any)[key];
    if (typeof value === 'string') {
      (sanitizedClient as any)[key] = value.replace(/\t/g, '').trim() || null;
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      (sanitizedClient as any)[key] = sanitizeClientDTO(value);
    } else if (Array.isArray(value)) {
      (sanitizedClient as any)[key] = value.map((item: any) => {
        if (typeof item === 'string') {
          return item.replace(/\t/g, '').trim() || null;
        } else if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
          return sanitizeClientDTO(item);
        }
        return item;
      });
    }
  });

  return sanitizedClient;
}

export const ClientFormWrapper = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const { clientId = '' } = useParams();
  const isEdit = Boolean(clientId);
  const navigate = useNavigate();
  const { data } = useAppSelector((state) => state.clients.client);

  const onSubmit = async (values: FormValues) => {
    const client = sanitizeClientDTO({
      ...values,
      currentLocation: values.sameAsPrimaryAddress ? values.primaryAddress : values.currentLocation,
      insurances: []
    });
    console.log({ client, values });
    if (clientId) {
      await dispatch(putEditClient({ clientId, client }));
    } else {
      const response = await dispatch(
        postCreateClient({
          client
        })
      );
      if (response.meta.requestStatus === 'fulfilled') {
        dispatch(setChangeStatusModal('changeStatus'));
        navigate(`/clients/edit/${response.payload.id}/profile`);
      }
    }
  };

  const normalizedInitialValues = (NewData?: FormValues | null): FormValues => ({
    ...initialValues,
    ...(NewData || {}),
    careGaps: NewData?.careGaps ?? initialValues.careGaps,
    emergencyContacts:
      NewData?.emergencyContacts && NewData?.emergencyContacts?.length > 0
        ? NewData.emergencyContacts
        : initialValues.emergencyContacts,
    billing: {
      ...initialValues.billing,
      ...(NewData?.billing || {}),
      orderingProviders:
        NewData?.billing?.orderingProviders && NewData?.billing?.orderingProviders?.length > 0
          ? NewData.billing.orderingProviders
          : initialValues.billing.orderingProviders,
      billingPrograms:
        NewData?.billing?.billingPrograms && NewData?.billing?.billingPrograms?.length > 0
          ? NewData.billing.billingPrograms
          : initialValues.billing.billingPrograms,
      billingSites:
        NewData?.billing?.billingSites && NewData?.billing?.billingSites?.length > 0
          ? NewData.billing.billingSites
          : initialValues.billing.billingSites,
      zCodes:
        NewData?.billing?.zCodes && NewData?.billing?.zCodes?.length > 0
          ? NewData.billing.zCodes
          : initialValues.billing.zCodes
    }
  });

  return (
    // @ts-expect-error formik types are incorrect when you pass component={null}
    <Formik
      // @ts-expect-error formik types are incorrect when you pass component={null}
      enableReinitialize
      // @ts-expect-error formik types are incorrect when you pass component={null}
      validateOnMount
      // @ts-expect-error formik types are incorrect when you pass component={null}
      validationSchema={validationSchema}
      // @ts-expect-error formik types are incorrect when you pass component={null}
      component={null}
      // @ts-expect-error formik types are incorrect when you pass component={null}
      initialValues={isEdit ? normalizedInitialValues(data) : initialValues}
      // @ts-expect-error formik types are incorrect when you pass component={null}
      onSubmit={onSubmit}
    >
      {children}
    </Formik>
  );
};

const ClientForm = () => {
  const { isMobile } = useResponsive();
  const { isMobileInfoBannerClosed, setIsMobileInfoBannerClosed } = useMobileInfoBanner();
  const [loading, setLoading] = useState(false);
  const { clientId = '' } = useParams();
  const { data } = useAppSelector((state) => state.clients.client);
  const currentTab = useRouteMatch(['/profile', '/ourWork']);
  const dispatch = useAppDispatch();
  const { organizationId } = getLsUserRoleObject();
  const isEdit = Boolean(clientId);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const promises = [
        dispatch(getBillingConfig({ organizationId })),
        dispatch(getBillingProviders({ organizationId })),
        dispatch(getSites()),
        dispatch(getFavoriteZCodes())
      ];

      await Promise.all(
        isEdit
          ? [
              dispatch(getScreenerDetails({ clientId, type: 'impact' })),
              dispatch(getScreenerHistory({ clientId, type: 'impact' })),
              dispatch(getScreeners({ clientId })),
              dispatch(getScreenerQuestions({ clientId, type: 'impact' })),
              dispatch(getOtherScreenerDetails({ clientId })),
              dispatch(getOtherScreenerHistory({ clientId })),
              dispatch(getClient({ organizationId, clientId })),
              dispatch(getClientHistory({ clientId })),
              dispatch(getTodos({ clientId })),
              dispatch(getGoals({ clientId })),
              dispatch(getNewTodoDraft({ clientId, creationContext: 'ROADMAPS' })),
              dispatch(getNewGoalDraft({ clientId })),
              dispatch(getPresets({ clientId })),
              ...promises
            ]
          : promises
      );
      setLoading(false);
    };

    fetchData();

    return () => {
      dispatch(resetClient());
    };
  }, [isEdit, clientId]);

  if (loading) {
    return (
      <Stack width="100%" display="flex" flexDirection="row" justifyContent="center" mt="120px">
        <LoadingSpinner />
      </Stack>
    );
  }

  return (
    <Stack>
      {!isMobileInfoBannerClosed && isMobile && (
        <div className={style.mobileInfoBanner}>
          <Text weight="regular" size="large" className={style.mobileInfoText}>
            Having trouble seeing the app in mobile? Try turning your phone to landscape.
          </Text>
          <button onClick={() => setIsMobileInfoBannerClosed(true)}>
            <span className={clsx('material-icons', style.mobileInfoIcon)}>close</span>
          </button>
        </div>
      )}
      <div>
        <Breadcrumbs
          breadcrumbItems={[{ name: 'Clients', path: '/clients' }]}
          breadcrumbLastItem={isEdit ? `${data?.firstName} ${data?.lastName}` : 'New Client'}
          withBackground
        />
      </div>
      <Stack display="flex" flexDirection="row" gap="12px">
        <ClientFormWrapper>
          <Stack display="flex" flexDirection="row" gap="12px" width="100%">
            <Stack
              flex={3}
              position="relative"
              gap="12px"
              display="flex"
              alignItems="center"
              flexDirection="column"
            >
              <ProfileInfo />
            </Stack>
            <Stack flex={9}>
              <Tabs
                data-test-id="profile-tabs"
                sx={{
                  backgroundColor: 'white',
                  borderBottom: '2px solid #F1F5F9',
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#188AA1',
                    height: '3px'
                  },
                  '& a[aria-selected="true"]': {
                    color: '#188AA1',
                    backgroundColor: '#F8FAFC',
                    textTransform: 'none',
                    fontFamily: '"ProximaNova", sans-serif',
                    fontWeight: 600,
                    padding: '20px 0px',
                    pointerEvents: 'all'
                  },
                  '& a': {
                    height: '40px',
                    color: '#8DA0B9',
                    backgroundColor: '#fff',
                    textTransform: 'none',
                    fontFamily: '"ProximaNova", sans-serif',
                    fontWeight: 600,
                    padding: '20px 0px',
                    pointerEvents: 'all'
                  }
                }}
                value={currentTab}
                aria-label="nav tabs example"
              >
                {clientId && (
                  <Tab
                    content="Our Work"
                    value="/ourWork"
                    path={`/clients/edit/${clientId}/ourWork`}
                    component={LinkWithIndirectRouting}
                  />
                )}
                <Tab
                  content="Profile"
                  value="/profile"
                  path={clientId ? `/clients/edit/${clientId}/profile` : '/clients/new/profile'}
                  component={LinkWithIndirectRouting}
                />
              </Tabs>
              <div>
                <Outlet />
              </div>
            </Stack>
          </Stack>
        </ClientFormWrapper>
      </Stack>
    </Stack>
  );
};

export default ClientForm;
